
import React from 'react';
import { ProductSpecs } from '../ProductSpecs';
import { ProductReviews } from '../ProductReviews';
import { ProductFAQ } from '../ui/ProductFAQ';
import { BuyingGuide } from '../ui/BuyingGuide';
import { getProductFeatures, getProductUseCases, getProductFAQ, getProductReviewsSummary } from '../../utils/dataFallbacks';

export function ProductTabs({ activeTab, setActiveTab, product }) {
  const { warranty, release_date, asin, rating, review_count } = product;
  
  // Use enriched data with fallbacks
  const features = getProductFeatures(product);
  const useCases = getProductUseCases(product);
  const faqs = getProductFAQ(product);
  const reviewsSummary = getProductReviewsSummary(product);

  return (
    <div className="mt-12">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8 overflow-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-blue-500 scrollbar-track-transparent">
          <button 
            onClick={() => setActiveTab('features')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'features'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-features"
          >
            Features
          </button>
          <button 
            onClick={() => setActiveTab('specifications')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'specifications'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-specifications"
          >
            Specifications
          </button>
          <button 
            onClick={() => setActiveTab('reviews')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'reviews'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-reviews"
          >
            Reviews
          </button>
          <button 
            onClick={() => setActiveTab('use-cases')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'use-cases'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-use-cases"
          >
            Use Cases
          </button>
          <button 
            onClick={() => setActiveTab('faq')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'faq'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-faq"
          >
            FAQ
          </button>
          <button 
            onClick={() => setActiveTab('buying-guide')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'buying-guide'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            data-analytics-id="tab-buying-guide"
          >
            Buying Guide
          </button>
        </nav>
      </div>
      
      {/* Tab Content */}
      <div className="py-6 prose max-w-none">
        {/* Features Tab */}
        {activeTab === 'features' && (
          <div>
            {features?.length > 0 ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">About this item</h3>
                <ul className="list-disc list-outside ml-6 space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="text-gray-600 dark:text-gray-400 leading-7">{feature}</li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No detailed features available for this product.</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  Please check the product page on Amazon for complete details.
                </p>
              </div>
            )}

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-6">
              {warranty && (
                <div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Warranty:</span>
                  <p className="text-gray-600 dark:text-gray-400">{warranty}</p>
                </div>
              )}
              {release_date && (
                <div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Release Date:</span>
                  <p className="text-gray-600 dark:text-gray-400">{release_date}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Specifications Tab */}
        {activeTab === 'specifications' && (
          <ProductSpecs product={product} />
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div>
            <ProductReviews asin={asin} rating={rating} reviewCount={review_count} />
            {/* Enhanced Reviews Summary */}
            {reviewsSummary && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Customer Insights</h4>
                <p className="text-blue-800 dark:text-blue-200">{reviewsSummary}</p>
              </div>
            )}
          </div>
        )}

        {/* Use Cases Tab */}
        {activeTab === 'use-cases' && (
          <div>
            {useCases?.length > 0 ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Perfect for these scenarios</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {useCases.map((useCase, index) => (
                    <div key={index} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 dark:text-green-400 text-xl">✓</span>
                        <span className="text-gray-800 dark:text-gray-200 font-medium capitalize">{useCase}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No specific use cases available for this product.</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  This product can be used for general purposes in its category.
                </p>
              </div>
            )}
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <ProductFAQ product={product} faqs={faqs} />
        )}

        {/* Buying Guide Tab */}
        {activeTab === 'buying-guide' && (
          <BuyingGuide product={product} category={product.category_slug} />
        )}
      </div>
    </div>
  );
}
